import { Injectable } from '@angular/core';
import { TranslateLoader, TranslationObject } from '@ngx-translate/core';
import { Observable, catchError, from, map, of } from 'rxjs';
import en from './i18n/en.json';

type BundleImport = () => Promise<{ default: unknown }>;

/**
 * English is compiled in: it is the fallback for every missing key and the language most users run. Every
 * other bundle is its own lazy chunk, so a user downloads only the language they read rather than all twenty.
 */
const LAZY_BUNDLES: ReadonlyMap<string, BundleImport> = new Map<string, BundleImport>([
  ['ar', () => import('./i18n/ar.json')],
  ['de', () => import('./i18n/de.json')],
  ['es', () => import('./i18n/es.json')],
  ['fil', () => import('./i18n/fil.json')],
  ['fr', () => import('./i18n/fr.json')],
  ['ja', () => import('./i18n/ja.json')],
  ['kk', () => import('./i18n/kk.json')],
  ['km', () => import('./i18n/km.json')],
  ['ms', () => import('./i18n/ms.json')],
  ['nl', () => import('./i18n/nl.json')],
  ['pt', () => import('./i18n/pt.json')],
  ['ru', () => import('./i18n/ru.json')],
  ['si', () => import('./i18n/si.json')],
  ['ta', () => import('./i18n/ta.json')],
  ['th', () => import('./i18n/th.json')],
  ['ur', () => import('./i18n/ur.json')],
  ['vi', () => import('./i18n/vi.json')],
  ['zh-Hans', () => import('./i18n/zh-Hans.json')],
  ['zh-Hant', () => import('./i18n/zh-Hant.json')],
]);

@Injectable()
export class LanguageLoader implements TranslateLoader {
  getTranslation(lang: string): Observable<TranslationObject> {
    const load = LAZY_BUNDLES.get(lang);
    if (!load) return of(en as TranslationObject);

    return from(load()).pipe(
      map(bundle => bundle.default as TranslationObject),
      // A chunk that fails to load leaves the language empty, so every key shows its English fallback.
      catchError(() => of({} as TranslationObject)),
    );
  }
}
